/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Radio,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useMemo } from "react";
import { formatFieldName } from "../../../../utils/formatFieldName";

const NewMergeTable = ({ demoCaseData, onFieldSelect, selectedValues }) => {
  const fieldBg = useColorModeValue("gray.300", "gray.700");
  const headerBg = useColorModeValue("gray.300", "gray.800");

  const skipFields = [
    "_etag",
    "_rid",
    "_self",
    "_ts",
    "_attachments",
    "SECURITY_PARTITION",
    "id",
    "PERSON_ID",
    "CASE_URL",
  ]; // List the fields you want to skip

  const fields = useMemo(() => {
    const allFields = Array.from(
      new Set(demoCaseData.flatMap((item) => Object.keys(item)))
    );

    return allFields.filter(
      (field) =>
        !skipFields.includes(field) &&
        demoCaseData.some(
          (caseItem) => caseItem[field] != null && caseItem[field] !== ""
        )
    );
  }, [demoCaseData]);

  const columns = useMemo(() => {
    const getFieldValueSet = (field) => {
      return new Set(
        demoCaseData.map((caseItem) => caseItem[field])
        // .filter((v) => v != null && v !== "")
      );
    };

    const isFieldConflicted = (field) => getFieldValueSet(field).size > 1;

    return [
      {
        id: "fieldName",
        header: "Field Name",
        accessorFn: (row, index) => fields[index],
        size: 200,
        cell: ({ row }) => {
          const field = fields[row.index];
          return (
            <Td
              position="sticky"
              left={0}
              zIndex={1}
              bg={fieldBg}
              fontWeight={600}
              style={{
                width: "200px",
                boxShadow: "2px 0 5px rgba(0, 0, 0, 0.1)",
                maxWidth: "200px",
                whiteSpace: "normal",
                wordBreak: "break-word",
              }}
            >
              {formatFieldName(field)}
            </Td>
          );
        },
      },
      ...demoCaseData?.map((_, caseIndex) => ({
        id: `case${caseIndex + 1}`,
        header: `Case ${caseIndex + 1}`,
        size: 350,
        cell: ({ row }) => {
          const field = fields[row.index];
          const value = demoCaseData[caseIndex][field];
          const conflicted = isFieldConflicted(field); // ✅ Use it here

          return (
            <Td
              style={{
                width: "350px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                color: conflicted ? "red" : "inherit", // ✅ Highlight if conflict
              }}
            >
              <Radio
                name={field}
                value={value}
                isChecked={selectedValues[field] === value}
                onChange={() => onFieldSelect(field, value)}
              >
                {value}
              </Radio>
            </Td>
          );
        },
      })),
    ];
  }, [demoCaseData, fields, fieldBg, onFieldSelect, selectedValues]);

  const data = useMemo(() => fields.map((field) => ({ field })), [fields]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    // defaultColumn: {
    //   minSize: 100,
    //   maxSize: 9999,
    // },
  });

  return (
    <Box overflowX="auto">
      <TableContainer
        maxHeight="480px"
        overflowY="auto"
        scrollBehavior="smooth"
      >
        <Table
          variant="simple"
          size="sm"
          style={{
            tableLayout: "fixed", // Ensure fixed layout to handle widths
            width: "100%",
          }}
        >
          <Thead
            position="sticky"
            top={0}
            zIndex={2}
            bg={`${headerBg}!important`}
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th
                    key={header.id}
                    colSpan={header.colSpan}
                    style={{
                      width: header.getSize(),
                      position: header.id === "fieldName" ? "sticky" : "static",
                      left: header.id === "fieldName" ? 0 : undefined,
                      zIndex: header.id === "fieldName" ? 1 : undefined,
                      background:
                        header.id === "fieldName" ? headerBg : undefined,
                      boxShadow:
                        header.id === "fieldName"
                          ? "2px 0 5px rgba(0, 0, 0, 0.1)"
                          : undefined,
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    }}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => (
              <Tr key={row.id}>
                {row
                  .getVisibleCells()
                  .map((cell) =>
                    flexRender(cell.column.columnDef.cell, cell.getContext())
                  )}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default NewMergeTable;
